import { useState, useEffect } from "react";
import { destroy, mutate, query } from "../../defaults/fetch";

// ===== User Benefits Hooks =====

export const useGetUserBenefits = (userId, flag) => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserBenefits = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const url = `user-benefit/user/${userId}`;
        const response = await query(url);
        if (response && response.data) {
          setBenefits(response.data);
        }
      } catch (error) {
        console.error("Error fetching user benefits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserBenefits();
  }, [userId, flag]);

  return { loading, benefits };
};

/**
 * Hook to add user benefits
 * @returns {Function} addUserBenefit - Function to add user benefits
 * @param {Object} data - The benefit data
 * @param {number} data.userId - The user ID
 * @param {number[]} data.benefitIds - Array of benefit IDs to add
 */
export const useAddUserBenefit = () => {
  const addUserBenefit = async (data) => {
    try {
      const url = `user-benefit/add`;
      const response = await mutate(url, true, data);
      return response;
    } catch (error) {
      console.error("Error adding user benefit:", error);
      throw error;
    }
  };

  return addUserBenefit;
};

export const useRemoveUserBenefit = () => {
  const removeUserBenefit = async (userId, benefitId) => {
    try {
      const url = `user-benefit/remove/${userId}/${benefitId}`;
      const response = await destroy(url);
      return response;
    } catch (error) {
      console.error("Error removing user benefit:", error);
      throw error;
    }
  };

  return removeUserBenefit;
};

// ===== User Deductions Hooks =====

export const useGetUserDeductions = (userId, flag) => {
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserDeductions = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const url = `user-deduction/user/${userId}`;
        const response = await query(url);
        if (response && response.data) {
          setDeductions(response.data);
        }
      } catch (error) {
        console.error("Error fetching user deductions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDeductions();
  }, [userId, flag]);

  return { loading, deductions };
};

/**
 * Hook to add user deductions
 * @returns {Function} addUserDeduction - Function to add user deductions
 * @param {Object} data - The deduction data
 * @param {number} data.userId - The user ID
 * @param {number[]} data.deductionIds - Array of deduction IDs to add
 */
export const useAddUserDeduction = () => {
  const addUserDeduction = async (data) => {
    try {
      const url = `user-deduction/add`;
      const response = await mutate(url, true, data);
      return response;
    } catch (error) {
      console.error("Error adding user deduction:", error);
      throw error;
    }
  };

  return addUserDeduction;
};

export const useRemoveUserDeduction = () => {
  const removeUserDeduction = async (userId, deductionId) => {
    try {
      const url = `user-deduction/remove/${userId}/${deductionId}`;
      const response = await destroy(url);
      return response;
    } catch (error) {
      console.error("Error removing user deduction:", error);
      throw error;
    }
  };

  return removeUserDeduction;
};
